import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { FaSearch, FaTimes, FaPlus, FaTrash, FaUndo } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { MdOutlineRule } from "react-icons/md";
import AdminPageLoader from './AdminPageLoader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0
  }).format(amount);
}

const ComplimentaryProductsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [complimentaryProducts, setComplimentaryProducts] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [addingId, setAddingId] = useState(null);
  const [activeList, setActiveList] = useState('list1');
  const navigate = useNavigate();

  const debounceTimeout = useRef();

  const token = localStorage.getItem('authToken');

  // Fetch all complimentary products on mount
  useEffect(() => {
    fetchComplimentaryProducts();
  }, []);

  // Filter products by active list
  const filteredProducts = complimentaryProducts.filter(p => p.listType === activeList);

  const fetchComplimentaryProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/complimentary-products`);
      setComplimentaryProducts(response.data);
    } catch (error) {
      setMessage('Failed to fetch complimentary products.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchDebounced = (value) => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      if (value) {
        handleSearch(value);
      } else {
        setSearchResults([]);
      }
    }, 400);
  };

  const handleSearch = async (value = searchTerm) => {
    if (!value) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/product-list?search=${encodeURIComponent(value)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Exclude products already in complimentary collection (by original productId if present)
      const filtered = response.data.filter(prod =>
        !complimentaryProducts.some(c => c.productId === prod._id || (c.name === prod.name && c.brand === prod.brand && c.category === prod.category))
      );
      setSearchResults(filtered);
    } catch (error) {
      setMessage('Failed to search products.');
    } finally {
      setSearching(false);
    }
  };

  // Add a product to the complimentary collection
  const handleAddProduct = async (prod) => {
    setAddingId(prod._id);
    try {
      // Clone all relevant product fields so complimentary items stay in sync with the source product
      const payload = {
        productId: prod._id,
        name: prod.name,
        brand: prod.brand,
        category: prod.category,
        parentCategory: prod.parentCategory,
        tags: Array.isArray(prod.tags) ? prod.tags : [],
        price: prod.price,
        originalPrice: prod.originalPrice,
        availableStock: prod.availableStock,
        quantity: prod.quantity,
        unit: prod.unit,
        description: prod.description,
        image1: prod.image1,
        image2: prod.image2,
        image3: prod.image3,
        image4: prod.image4,
        thumbnail: prod.thumbnail,
        video: prod.video,
        rating: prod.rating,
        listType: activeList,
      };
      const response = await axios.post(`${API_BASE_URL}/complimentary-product`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(response.data.message || 'Product added.');
      fetchComplimentaryProducts();
      setSearchResults([]);
      setSearchTerm('');
    } catch (error) {
      setMessage('Failed to add product.');
    } finally {
      setAddingId(null);
    }
  };

  // Remove a complimentary product
  const handleRemoveProduct = async (id) => {
    setRemovingId(id);
    try {
      const response = await axios.delete(`${API_BASE_URL}/complimentary-product/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(response.data.message || 'Product removed.');
      fetchComplimentaryProducts();
    } catch (error) {
      setMessage('Failed to remove product.');
    } finally {
      setRemovingId(null);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setMessage('');
  };

  return (
    <div className="container-fluid p-3 pt-2 g-0 adma">
      {/* Heading */}
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div className="section-title">
              <h4 className="mt-1">FREE PRODUCTS ({filteredProducts.length} TOTAL)</h4>
              <p className="text-muted p-0 m-0">Add existing products to your free product lists</p>
            </div>
            <button
              className="theme-btn-blue-flat btn-sm rounded-0"
              onClick={() => navigate('/admin/complimentary-rules')}
              title="Set Rules"
              style={{ height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', textTransform: 'uppercase', padding: '0 16px' }}
            >
              <MdOutlineRule className="me-2" style={{ fontSize: '16px' }} />
              Set Rules
            </button>
          </div>
        </div>
      </div>

      {/* List Tabs */}
      <div className="d-flex align-items-center gap-1 mt-2 mb-0" style={{ borderBottom: '1px solid #d1d5db', paddingBottom: '8px', paddingLeft: '12px', backgroundColor: '#ffffff' }}>
        {['list1', 'list2', 'list3'].map((listKey, idx) => {
          const listLabel = `Free List ${idx + 1}`;
          const isActive = activeList === listKey;
          return (
            <button
              key={listKey}
              type="button"
              onClick={() => setActiveList(listKey)}
              style={{
                border: 'none',
                background: isActive ? '#e8eaf6' : 'transparent',
                color: isActive ? '#1a237e' : '#475569',
                fontWeight: isActive ? '700' : '600',
                fontSize: '13px',
                padding: '8px 16px',
                borderRadius: '4px',
                transition: 'all 0.2s',
                textTransform: 'uppercase'
              }}
            >
              {listLabel}
            </button>
          );
        })}
      </div>

      {/* Control Panel — cloned from Products/Customers/Top Picks pages */}
      <div 
        className="mt-2" 
        style={{ 
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #d1d5db',
          padding: '5px 12px 12px 12px',
          gap: '12px',
          boxSizing: 'border-box'
        }}
      >
        <div className="control-panel-search" style={{ margin: 0 }}>
          <span
            className="position-absolute top-50 translate-middle-y start-0 ps-3 text-muted"
            style={{ pointerEvents: 'none' }}
          >
            <FaSearch style={{ fontSize: '14px' }} />
          </span>
          <input
            type="text"
            className="form-control form-control-sm ps-5"
            placeholder="Search by product name, brand, or category to add..."
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              height: '38px',
              fontSize: '14px',
              background: '#ffffff',
              width: '100%',
              margin: 0
            }}
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              handleSearchDebounced(e.target.value);
            }}
          />
          {searchTerm && (
            <button
              className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 p-0"
              style={{ zIndex: 2, border: 'none', background: 'none', color: '#94a3b8', margin: 0 }}
              onClick={handleClearSearch}
              tabIndex={-1}
            >
              <FaTimes />
            </button>
          )}
        </div>
        <button
          type="button"
          className="btn-reset-square"
          title="Reset Search"
          onClick={handleClearSearch}
          style={{ height: '38px', width: '38px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: 0 }}
        >
          <FaUndo style={{ fontSize: '14px' }} />
        </button>
        <div className="control-panel-reset" style={{ marginLeft: 'auto', margin: 0 }}>
          <small className="text-muted" style={{ fontSize: '13px', whiteSpace: 'nowrap', margin: 0 }}>
            Showing {filteredProducts.length} products in Free List {activeList === 'list1' ? '1' : activeList === 'list2' ? '2' : '3'}
          </small>
        </div>
      </div>

      {/* Search Results Table */}
      {(searching || searchResults.length > 0 || (searchTerm && !searching)) && (
        <div className="row orders-table-row mt-3">
          <div className="col-12">
            <div className="card-header py-2 px-3" style={{ background: '#e8eaf6', border: '1px solid #d1d5db', borderBottom: 'none' }}>
              <strong style={{ color: '#1a237e', fontSize: '13px', textTransform: 'uppercase' }}>
                Search Results ({searchResults.length} Found)
              </strong>
            </div>
            <div className="table-responsive" style={{ border: '1px solid #d1d5db' }}>
              {searching ? (
                <div className="text-center my-4 py-3 bg-white">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ms-2 text-muted" style={{ fontSize: '14px' }}>Searching products...</span>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-4 bg-white text-muted" style={{ fontSize: '13px' }}>
                  No products found matching your search.
                </div>
              ) : (
                <table className="table table-hover orders-table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '8%', minWidth: '50px' }}>#</th>
                      <th style={{ width: '37%', minWidth: '220px' }}>Product Info</th>
                      <th style={{ width: '25%', minWidth: '180px' }}>Category & Brand</th>
                      <th style={{ width: '12%', minWidth: '100px' }}>Price</th>
                      <th style={{ width: '10%', minWidth: '100px' }}>Quantity</th>
                      <th style={{ width: '8%', minWidth: '80px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((prod, idx) => (
                      <tr key={prod._id}>
                        <td>
                          <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                            {idx + 1}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            <div style={{ width: '40px', height: '40px', background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                              <img 
                                src={`${API_BASE_URL}/${prod.thumbnail}`} 
                                alt={prod.name} 
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/40?text=P';
                                }}
                              />
                            </div>
                            <div className="d-flex flex-column align-items-start">
                              <strong style={{ color: '#1a237e', fontSize: '14px' }}>{prod.name}</strong>
                              {prod.description && (
                                <span className="text-muted text-start" style={{ fontSize: '11px', marginTop: '2px', display: 'block', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {prod.description}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1 align-items-start">
                            <div className="d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                              <span className="text-muted" style={{ fontWeight: '500' }}>Category:</span>
                              <span style={{ fontWeight: '600', color: '#0f172a' }}>{prod.category || 'N/A'}</span>
                            </div>
                            <div className="d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                              <span className="text-muted" style={{ fontWeight: '500' }}>Brand:</span>
                              <span style={{ fontWeight: '600', color: '#92400e' }}>{prod.brand || 'N/A'}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-start">
                            <span style={{ fontWeight: '700', color: '#16a34a', fontSize: '15px' }}>
                              {formatCurrency(prod.price)}
                            </span>
                            {prod.originalPrice && (
                              <span className="text-muted" style={{ fontSize: '11px', textDecoration: 'line-through', marginTop: '1px' }}>
                                MRP: {formatCurrency(prod.originalPrice)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-start">
                            <span className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
                              {prod.quantity} {prod.unit}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn-action-square btn-action-accept"
                              title="Add to Free List"
                              onClick={() => handleAddProduct(prod)}
                              disabled={addingId === prod._id}
                            >
                              {addingId === prod._id ? (
                                <div className="spinner-border spinner-border-sm text-success" role="status" style={{ width: '12px', height: '12px' }}></div>
                              ) : (
                                <FaPlus size={12} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table of selected complimentary products */}
      {loading ? (
        <AdminPageLoader message="Loading free products..." />
      ) : (
        <div className="row orders-table-row mt-4">
          <div className="col-12">
            <div className="card-header py-2 px-3" style={{ background: '#e8eaf6', border: '1px solid #d1d5db', borderBottom: 'none' }}>
              <strong style={{ color: '#1a237e', fontSize: '13px', textTransform: 'uppercase' }}>
                Selected Free Products in Free List {activeList === 'list1' ? '1' : activeList === 'list2' ? '2' : '3'}
              </strong>
            </div>
            <div className="table-responsive" style={{ border: '1px solid #d1d5db' }}>
              <table className="table table-hover orders-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '8%', minWidth: '50px' }}>#</th>
                    <th style={{ width: '37%', minWidth: '220px' }}>Product Info</th>
                    <th style={{ width: '25%', minWidth: '180px' }}>Category & Brand</th>
                    <th style={{ width: '12%', minWidth: '100px' }}>Price</th>
                    <th style={{ width: '10%', minWidth: '100px' }}>Quantity</th>
                    <th style={{ width: '8%', minWidth: '80px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-5 text-muted bg-white" style={{ fontSize: '14px' }}>
                        No products in this list yet. Search and add products above.
                      </td>
                    </tr>
                  ) : (
                    filteredProducts.map((prod, idx) => (
                      <tr key={prod._id}>
                        <td>
                          <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                            {idx + 1}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            <div style={{ width: '40px', height: '40px', background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                              <img 
                                src={`${API_BASE_URL}/${prod.thumbnail}`} 
                                alt={prod.name} 
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/40?text=P';
                                }}
                              />
                            </div>
                            <div className="d-flex flex-column align-items-start">
                              <strong style={{ color: '#1a237e', fontSize: '14px' }}>{prod.name}</strong>
                              {prod.description && (
                                <span className="text-muted text-start" style={{ fontSize: '11px', marginTop: '2px', display: 'block', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {prod.description}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1 align-items-start">
                            <div className="d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                              <span className="text-muted" style={{ fontWeight: '500' }}>Category:</span>
                              <span style={{ fontWeight: '600', color: '#0f172a' }}>{prod.category || 'N/A'}</span>
                            </div>
                            <div className="d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                              <span className="text-muted" style={{ fontWeight: '500' }}>Brand:</span>
                              <span style={{ fontWeight: '600', color: '#92400e' }}>{prod.brand || 'N/A'}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-start">
                            <span style={{ fontWeight: '700', color: '#16a34a', fontSize: '15px' }}>
                              {formatCurrency(prod.price)}
                            </span>
                            {prod.originalPrice && (
                              <span className="text-muted" style={{ fontSize: '11px', textDecoration: 'line-through', marginTop: '1px' }}>
                                MRP: {formatCurrency(prod.originalPrice)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-start">
                            <span className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
                              {prod.quantity} {prod.unit}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn-action-square btn-action-cancel"
                              title="Remove from Free List"
                              onClick={() => handleRemoveProduct(prod._id)}
                              disabled={removingId === prod._id}
                            >
                              {removingId === prod._id ? (
                                <div className="spinner-border spinner-border-sm text-danger" role="status" style={{ width: '12px', height: '12px' }}></div>
                              ) : (
                                <FaTrash size={12} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {message && <div className="mt-2 text-danger">{message}</div>}
    </div>
  );
};

export default ComplimentaryProductsPage;